import {
    ActionReducerMapBuilder,
    AsyncThunk,
    createAsyncThunk,
    createEntityAdapter,
    createSelector,
    createSlice,
    EntityId,
    PayloadAction,
    Selector,
    SliceCaseReducers,
    ValidateSliceCaseReducers
} from "@reduxjs/toolkit";
import { createSelectorCreator, weakMapMemoize } from "reselect";
import randomstring from "randomstring";
import sift from "sift";
import { sort } from "sift-sort";
import { RootState, ThunkAppDispatch } from "./store";
import {
    createEntity,
    deleteEntity,
    findEntity,
    NullableId,
    Paginated,
    patchEntity
} from "./api";
import { Draft } from '@reduxjs/toolkit';
import { set } from "lodash";
type WritableDraft<T> = {
    -readonly [K in keyof T]: Draft<T[K]>;
};
export const createDeepEqualSelector = createSelectorCreator({
    memoize: weakMapMemoize,
    argsMemoize: weakMapMemoize
})

interface Dictionary<T> {
    [key: string]: T;
}

export interface DBThunks<T extends BaseEntity> {
    create: AsyncThunk<T, T, any>;
    patch: AsyncThunk<T, DBThunkInput<Partial<T>, PatchOptions>, any>;
    delete: AsyncThunk<T, DBThunkInput<Partial<T>, DeleteOptions>, any>;
    find: AsyncThunk<any, any, any>;
    createOptimisticUpdate: AsyncThunk<T, T, any>;
    syncOptimisticUpdate: AsyncThunk<T, T, any>;
    undoOptimisticUpdate: AsyncThunk<string, string, any>;
}

export interface Asset {
    name: string;
    type: string;
    url: string;
}

export interface Update<T extends BaseEntity> {
    id: NullableId;
    changes: Partial<T>;
}

export interface BaseEntity extends Obj {
    _id?: NullableId;
    tempId?: string;
    version?: number;
    dirtySessionId?: string;
    dirty?: boolean;
}

export interface GenericState<T extends BaseEntity> {
    ids: EntityId[];
    entities: Dictionary<T>;
    itemsLoaded: boolean;
    itemsLoading: boolean;
    total?: number;
}

type Obj = Record<string, any>;

export interface DeleteOptions {
    inCaseOfErrorRollbackOptimisticUpdateDisabled: boolean;
}

export interface PatchOptions {
    disableOptimisticUpdates?: boolean;
}

export interface DBThunkInput<T, OptionsInterface> { entity: T, options?: OptionsInterface }

export const DBserviceAdapter = createEntityAdapter({
    selectId: (item: any) => item?._id ?? item?.tempId ?? null,
});

export interface HookOptions {
    dispatch: ThunkAppDispatch;
}
export interface Hooks {
    create?: (entity: any, options: HookOptions) => Promise<any> | any;
    patch?: (entity: any, options: HookOptions) => Promise<any> | any;
    delete?: (entity: any, options: HookOptions) => Promise<any> | any;
    find?: (entityList: any[], options: HookOptions) => Promise<any[]> | any;
}

export interface DBThunkHooks {
    before?: Hooks;
    after?: Hooks;
}

export type GetStateFn<T extends BaseEntity> = (state: any) => GenericState<T>;

export const _DBReducerMap: Map<string, any> = new Map();
export const createDBServiceSlice = <T extends BaseEntity>({
    service = "",
    initialState,
    hooks,
    customReducers,
    getReducerState,
    customThunks = {},
}: {
    service: string;
    initialState: GenericState<T>;
    hooks?: DBThunkHooks;
    getReducerState: GetStateFn<T>;
    customReducers?:
    | ((builder: ActionReducerMapBuilder<GenericState<T>>) => void)
    | undefined;
    customThunks?: { [key: string]: AsyncThunk<any, any, any> };
}) => {
    const selectors = DBserviceAdapter.getSelectors(getReducerState);
    const rootStateSelectors = DBserviceAdapter.getSelectors();

    const shouldEntityUpdate = (
        state: WritableDraft<GenericState<T>>,
        entity: T
    ): boolean => {
        let existingEntity;
        if (entity._id) {
            existingEntity = state.entities[entity._id];
            if (
                !existingEntity ||
                ((entity as BaseEntity)?.version ?? 0) > ((existingEntity as BaseEntity)?.version ?? 0)
            ) {
                return true;
            }
        }
        return false;
    };

    const createSliceFactory = <
        Reducers extends SliceCaseReducers<GenericState<T>>
    >(
        reducers: Reducers &
            ValidateSliceCaseReducers<GenericState<T>, Reducers> = {} as Reducers &
            ValidateSliceCaseReducers<GenericState<T>, Reducers>
    ) => {
        return createSlice({
            name: service,
            initialState,
            reducers: {
                addOne: DBserviceAdapter.addOne,
                addMany: DBserviceAdapter.addMany,
                setAll: DBserviceAdapter.setAll,
                removeOne: DBserviceAdapter.removeOne,
                removeMany: DBserviceAdapter.removeMany,
                removeAll: DBserviceAdapter.removeAll,
                updateOne: DBserviceAdapter.updateOne,
                updateMany: DBserviceAdapter.updateMany,
                upsertOne: DBserviceAdapter.upsertOne,
                upsertMany: DBserviceAdapter.upsertMany,
                clearState: (state) => {
                    DBserviceAdapter.removeAll(state);
                    state.itemsLoaded = false;
                },

                conditionalUpdateOne: (state, action: PayloadAction<T>) => {
                    const entity = action.payload;
                    if (shouldEntityUpdate(state, entity)) {
                        DBserviceAdapter.upsertOne(state, entity);
                    }
                },
                conditionalUpdateMany: (state, action: PayloadAction<T[]>) => {
                    DBserviceAdapter.updateMany(
                        state,
                        action.payload
                            .filter((entity) => shouldEntityUpdate(state, entity))
                            .map((entity) => ({
                                id: entity._id!,
                                changes: entity,
                            }))
                    );
                },
                setItemsLoaded: (state, action: PayloadAction<boolean>) => {
                    console.log(`[${service}] setItemsLoaded`);
                    state.itemsLoaded = action.payload;
                },
                setItemsLoading: (state, action: PayloadAction<boolean>) => {
                    state.itemsLoading = action.payload;
                },
                setTotal: (state, action: PayloadAction<number>) => {
                    state.total = action.payload;
                },
                ...customReducers,
            },
            extraReducers: customReducers,
        });
    };
    const slice = createSliceFactory();

    const createDBThunks = <T extends BaseEntity>(
        service: string
    ): DBThunks<T> => {
        return {
            find: createAsyncThunk(
                `${service}/find`,
                async (query: any, thunkAPI) => {
                    console.log(`[${service}] find`);
                    const dispatch: ThunkAppDispatch =
                        thunkAPI.dispatch as ThunkAppDispatch;

                    dispatch(slice.actions.setItemsLoading(true));

                    const getEntityPromise = dispatch(
                        findEntity.initiate({
                            service,
                            query: { ...query, breakCache: randomstring.generate(16) },
                        }))
                        .unwrap()
                        .then(async (response: Paginated<T> | T[] | undefined) => {
                            const data = (response && "data" in response) ? response.data : response;
                            const entityList =
                                (await hooks?.after?.find?.apply(null, [
                                    data ?? [],
                                    {
                                        dispatch,
                                    },
                                ])) ?? data ?? [];

                            dispatch(slice.actions.setTotal((response as Paginated<T>)?.total ?? entityList.length));

                            dispatch(slice.actions.addMany(entityList));
                            dispatch(slice.actions.updateMany(entityList.map((entity: T) => ({
                                id: entity._id,
                                changes: entity
                            }))));
                            dispatch(slice.actions.setItemsLoaded(true));
                            dispatch(slice.actions.setItemsLoading(false));

                            return entityList;
                        })
                        .catch((e) => {
                            console.error(e);
                            dispatch(slice.actions.setItemsLoading(false));
                        });
                    return getEntityPromise;
                }
            ),
            create: createAsyncThunk(
                `${service}/create2`,
                async (entity: T, thunkAPI) => {
                    console.log(`[${service}] Create2`);
                    entity.version = 1;
                    const dirtySessionId = randomstring.generate(16);
                    entity = { ...entity, dirtySessionId, dirty: true };

                    const dispatch: ThunkAppDispatch =
                        thunkAPI.dispatch as ThunkAppDispatch;

                    if (hooks?.before?.create) {
                        entity = await hooks?.before?.create(entity, { dispatch });
                    }

                    dispatch(
                        slice.actions.addOne({
                            ...entity,
                            _id: dirtySessionId,
                            tempId: dirtySessionId,
                        })
                    );

                    let response;
                    try {
                        let newEntity = await dispatch(
                            createEntity.initiate({
                                service,
                                entity,
                            })
                        ).unwrap();

                        newEntity = {
                            ...newEntity,
                            dirtySessionId: undefined,
                            dirty: false,
                        };

                        if (hooks?.after?.create) {
                            newEntity = await hooks?.after?.create(newEntity, { dispatch });
                        }

                        dispatch(
                            slice.actions.updateOne({
                                id: dirtySessionId,
                                changes: newEntity,
                            })
                        );

                        response = newEntity;
                    } catch (e) {
                        console.error(e);
                        dispatch(slice.actions.removeOne(dirtySessionId));

                        response = e;
                    }
                    return response;
                }
            ),
            patch: createAsyncThunk(
                `${service}/patch2`,
                async ({ entity, options }: DBThunkInput<Partial<T>, PatchOptions>, thunkAPI) => {
                    console.log(`[${service}] Patch`);

                    const dispatch: ThunkAppDispatch =
                        thunkAPI.dispatch as ThunkAppDispatch;

                    //const localState = getReducerState(getState());
                    const originalEntity = selectors.selectById(
                        thunkAPI.getState() as RootState,
                        entity._id
                    );
                    if (!originalEntity) {
                        console.log(`patch didnt found the entity - entity._id : ${entity._id}  not exists`)
                        return;
                    }
                    if (!originalEntity._id) {
                        console.log(`patch originalEntity didnt have _id. originalEntity:`)
                        console.log(originalEntity)
                        return;
                    }

                    const dirtySessionId = randomstring.generate(16);
                    entity = {
                        ...entity,
                        dirtySessionId,
                        dirty: true,
                        version: originalEntity?.version + 1 ?? 0,
                    };

                    if (hooks?.before?.patch) {
                        entity = await hooks?.before?.patch(entity, { dispatch });
                    }

                    if (!options?.disableOptimisticUpdates) {
                        console.log(`Update entity dark mode: ${JSON.stringify(entity?.isDarkMode)}`)
                        dispatch(
                            slice.actions.updateOne({
                                id: entity._id,
                                changes: entity,
                            })
                        );
                    }

                    let newEntity
                    try {
                        newEntity = await dispatch(
                            patchEntity.initiate({
                                service,
                                entity,
                            })
                        ).unwrap();

                        newEntity = {
                            ...newEntity,
                            dirtySessionId: undefined,
                            dirty: false,
                        };
                        if (hooks?.after) {
                            newEntity = await hooks?.after?.patch?.apply(null, [newEntity, { dispatch }]);
                        }
                        if (options?.disableOptimisticUpdates) {
                            dispatch(slice.actions.updateOne({
                                id: newEntity._id,
                                changes: newEntity
                            }));
                        } else {
                            dispatch(slice.actions.conditionalUpdateOne(newEntity));
                        }
                    } catch (e: any) {
                        dispatch(
                            slice.actions.updateOne({
                                id: originalEntity._id,
                                changes: originalEntity,
                            })
                        );
                        thunkAPI.rejectWithValue(e.data);
                        return e.data;
                    }
                    return newEntity;
                }
            ),
            delete: createAsyncThunk(
                `${service}/delete2`,
                async ({ entity, options }: DBThunkInput<Partial<T>, DeleteOptions>, thunkAPI) => {
                    console.log(`[${service}] delete`);

                    const dispatch: ThunkAppDispatch =
                        thunkAPI.dispatch as ThunkAppDispatch;

                    const originalEntity = selectors.selectById(
                        thunkAPI.getState(),
                        entity._id
                    );
                    const dirtySessionId = randomstring.generate(16);
                    entity = {
                        _id: entity._id,
                        dirtySessionId,
                        dirty: true,
                        deleted: true,
                        version: originalEntity?.version + 1 ?? 0,
                    } as any;

                    if (hooks?.before?.delete) {
                        entity = await hooks?.before?.delete(entity, { dispatch });
                    }

                    dispatch(slice.actions.removeOne(entity._id));

                    const deletedEntityPromise = dispatch(
                        deleteEntity.initiate({
                            service,
                            entity,
                        })
                    ).unwrap();
                    deletedEntityPromise
                        .then(async (newEntity: T) => {
                            newEntity = {
                                ...newEntity,
                                dirtySessionId: undefined,
                                dirty: false,
                            };
                            if (hooks?.after?.delete) {
                                newEntity = await hooks?.after?.delete(newEntity, { dispatch });
                            }
                            dispatch(slice.actions.removeOne(newEntity._id));
                        })
                        .catch((e) => {
                            // When an entity is not found, it means it was already removed so don't return it to the entity map

                            console.log(entity);
                            if (!(e?.data?.name === "NotFound" || options?.inCaseOfErrorRollbackOptimisticUpdateDisabled)) {
                                dispatch(slice.actions.addOne(originalEntity));
                            }
                            console.error(e);
                        });

                    return deletedEntityPromise;
                }
            ),
            createOptimisticUpdate: createAsyncThunk(
                `${service}/createOptimisticUpdate`,
                async (entity: T, thunkAPI) => {
                    console.log(`[${service}] makeOptimisticUpdate`);

                    const dirtySessionId = randomstring.generate(16);
                    entity = { ...entity, dirtySessionId, dirty: true };

                    const dispatch: ThunkAppDispatch = thunkAPI.dispatch as ThunkAppDispatch;
                    const dirtyEntity = {
                        ...entity,
                        _id: dirtySessionId,
                        tempId: dirtySessionId,
                    };

                    if (entity._id) {
                        dispatch(slice.actions.updateOne({
                            id: entity._id,
                            changes: dirtyEntity
                        }));
                    } else {
                        dispatch(slice.actions.addOne(dirtyEntity));
                    }
                    return entity;
                }
            ),
            syncOptimisticUpdate: createAsyncThunk(
                `${service}/syncOptimisticUpdate`,
                async (newEntity: T, thunkAPI) => {
                    console.log(`[${service}] syncOptimisticUpdate`);
                    const dirtySessionId = newEntity.dirtySessionId
                    newEntity = {
                        ...newEntity,
                        dirtySessionId: undefined,
                        dirty: false,
                    };

                    const dispatch: ThunkAppDispatch = thunkAPI.dispatch as ThunkAppDispatch;

                    // Remove the dirty entity using its dirtySessionId
                    dispatch(slice.actions.removeOne(dirtySessionId));

                    // Insert the newEntity using its actual _id
                    dispatch(slice.actions.addOne(newEntity));
                    return newEntity;
                }
            ),
            undoOptimisticUpdate: createAsyncThunk(
                `${service}/undoOptimisticUpdate`,
                async (dirtySessionId: string, thunkAPI) => {
                    console.log(`[${service}] undoOptimisticUpdate`);
                    const dispatch: ThunkAppDispatch = thunkAPI.dispatch as ThunkAppDispatch;

                    dispatch(slice.actions.removeOne(dirtySessionId));
                    return dirtySessionId;
                }
            ),

            ...customThunks,
        };
    };

    const dbThunks: DBThunks<T> = createDBThunks<T>(service);

    type DBReducerState = GenericState<T>;

    const stateSelector: Selector<DBReducerState, DBReducerState> = (
        state: DBReducerState
    ) => state;



    const getFilterObj: Selector = (_: DBReducerState, filterObj: string) => {
        return filterObj
    };

    const getProjection: Selector = (_: DBReducerState, __: string, projectionArray: string[]) => {
        return projectionArray
    };

    const getProjectionField: Selector = (_: DBReducerState, __: string, projectionField: string) => {
        return projectionField
    };

    const getSortObj: Selector = (_: DBReducerState, __: string, ___: string, sortObj: any) => {
        return sortObj
    };

    const selectByQuery = createDeepEqualSelector<
        [Selector<DBReducerState, DBReducerState>, typeof getFilterObj, typeof getProjection, typeof getSortObj],
        T[]
    >([stateSelector, getFilterObj, getProjection, getSortObj], (state: DBReducerState, filterObj: any, projectionArray: string[], sortObj: any = {}): T[] => {
        if (!filterObj || Object.keys(filterObj).length === 0) {
            //console.log(`filterObj param ${service} is empty`);
            return rootStateSelectors.selectAll(state);
        }
        if (!state) {
            //console.log(`state param ${service} is undefined`);
            return [];
        }
        if (!state.ids || state.ids.length === 0) {
            //console.log(`state.ids ${service} is empty`);
            return [];
        }

        let resultsArray = [];

        const siftFunction = sift(filterObj);
        resultsArray = (state.ids ?? [])
            .filter(id => siftFunction(state.entities[id]))
            .map(id => state.entities[id]) as T[];

        if (Object.keys(sortObj).length !== 0) {
            resultsArray = sort(resultsArray, sortObj);
        }

        if (Array.isArray(projectionArray) && projectionArray.length > 0) {
            resultsArray = resultsArray.map((entity: T | {}) => {
                let entityWithIndex = entity as any;  // or as {[key: string]: any} if you know the structure
                entityWithIndex = projectionArray.reduce((a: any, e: any) => {
                    a[e] = entityWithIndex[e];
                    return a;
                }, {});
                return entityWithIndex;
            }) as T[];
        }

        return resultsArray;

    });

    const selectByQueryOnlyLength = createSelector<
        [Selector<DBReducerState, DBReducerState>, typeof getFilterObj],
        number
    >([stateSelector, getFilterObj], (state: DBReducerState, filterObj: any): number => {

        return selectByQuery(state, filterObj).length

    });


    const selectOneObjectByQuery = createSelector<[Selector<DBReducerState, DBReducerState>, typeof getFilterObj, typeof getProjection, typeof getSortObj], T | null>
        ([stateSelector, getFilterObj, getProjection, getSortObj], (state: DBReducerState, filterObj: any, projectionArray: string[], sortObj: any = {}): T | null => {

            let queryResult = selectByQuery(state, filterObj, projectionArray, sortObj);

            if (queryResult.length >= 1) {
                const onlyOne: T = queryResult[0];
                return onlyOne;

            }

            return null;

        });


    const selectOneFieldByQuery = createSelector<[Selector<DBReducerState, DBReducerState>, typeof getFilterObj, typeof getProjectionField, typeof getSortObj], any>
        ([stateSelector, getFilterObj, getProjectionField, getSortObj], (state: DBReducerState, filterObj: any, projectionField: string, sortObj: any = {}): any => {

            if (!projectionField) {
                return null;
            }

            const onlyOne = selectOneObjectByQuery(state, filterObj, [projectionField], sortObj);

            if (!onlyOne) {
                return null;
            }

            if (onlyOne.hasOwnProperty(projectionField)) {
                return onlyOne[projectionField];
            }

            return null;

        });


    const getField: Selector = (_: DBReducerState, __: string, projection: string) => {
        return projection
    };

    const getOper: Selector = (_: DBReducerState, __: string, ___: string, sortObj: string) => {
        return sortObj
    };

    type MathOper = keyof Pick<typeof Math, 'max' | 'min'>;

    const selectByQueryOnlyMaxMin =
        createDeepEqualSelector<[Selector<DBReducerState, DBReducerState>, typeof getFilterObj, typeof getField, typeof getOper], number | null>
            ([stateSelector, getFilterObj, getField, getOper],
                (state: DBReducerState, filterObj: any, field: string, oper: MathOper): number | null => {

                    field = "order";
                    oper = "max";

                    const array = selectByQuery(state, filterObj)

                    if (array.length === 0)
                        return null;

                    const result = window.Math[oper](...array.map(o => o[field]))
                    return result

                });


    const dbReducer = { slice, dbThunks, selectByQuery, selectors, selectByQueryOnlyLength, selectByQueryOnlyMaxMin, selectOneObjectByQuery, selectOneFieldByQuery }
    _DBReducerMap.set(service, dbReducer);

    return dbReducer;
};

